import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { SubstanceDrugsDrugTypeModel } from 'forms/sections/substance/shared/DrugType';
import { isPVQForm } from 'helpers/form';

interface Props extends ValidationElementProps, Partial<SubstanceDrugsDrugTypeModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  formType?: string
}

export default class DrugType extends ValidationElement<Props> {
  update = (values: Partial<SubstanceDrugsDrugTypeModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        value: this.props.value,
        ...values,
      });
    }
  };

  updateDrugType = (values: SubstanceDrugsDrugTypeModel) => {
    this.update({ value: values.value });
  };

  updateDrugTypeOther = (values: SubstanceDrugsDrugTypeModel) => {
    this.update({ value: values.value || 'Other' });
  };

  isOther = (drug?: string) => {
    if (!drug) {
      return false;
    }

    return ![
      'Cocaine',
      'Stimulants',
      'THC',
      'Depressants',
      'Ketamine',
      'Narcotics',
      'Hallucinogenic',
      'Steroids',
      'Inhalants',
    ].includes(drug);
  };

  render() {
    const other = this.isOther(this.props.value);
    const selected = other ? 'Other' : this.props.value;
    const explanation = other && this.props.value !== 'Other' ? this.props.value : '';

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div className="drug-type">
        <RadioGroup
          name="born"
          attribute={`${this.props.attribute}.DrugType`}
          className="option-list option-list-vertical"
          onError={this.props.onError}
          required={this.props.required}
          selectedValue={selected}
        >
          <Radio
            className="cocaine"
            label={i18n.t('substance.pvq.drugs.drugType.label.cocaine')}
            value="Cocaine"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="stimulants"
            label={i18n.t('substance.pvq.drugs.drugType.label.stimulants')}
            value="Stimulants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="depressants"
            label={i18n.t('substance.pvq.drugs.drugType.label.depressants')}
            value="Depressants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="ketamine"
            label={i18n.t('substance.pvq.drugs.drugType.label.ketamine')}
            value="Ketamine"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="narcotics"
            label={i18n.t('substance.pvq.drugs.drugType.label.narcotics')}
            value="Narcotics"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="hallucinogenic"
            label={i18n.t('substance.pvq.drugs.drugType.label.hallucinogenic')}
            value="Hallucinogenic"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="steroids"
            label={i18n.t('substance.pvq.drugs.drugType.label.steroids')}
            value="Steroids"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="inhalants"
            label={i18n.t('substance.pvq.drugs.drugType.label.inhalants')}
            value="Inhalants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="other"
            label={i18n.t('substance.pvq.drugs.drugType.label.other')}
            value="Other"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
        </RadioGroup>
      </div>

    ) : (
      <div className="drug-type">
        <RadioGroup
          name="born"
          attribute={`${this.props.attribute}.DrugType`}
          className="option-list option-list-vertical"
          onError={this.props.onError}
          required={this.props.required}
          selectedValue={selected}
        >
          <Radio
            className="cocaine"
            label={i18n.t('substance.drugs.drugType.label.cocaine')}
            value="Cocaine"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="stimulants"
            label={i18n.t('substance.drugs.drugType.label.stimulants')}
            value="Stimulants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="thc"
            label={i18n.t('substance.drugs.drugType.label.thc')}
            value="THC"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="depressants"
            label={i18n.t('substance.drugs.drugType.label.depressants')}
            value="Depressants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="ketamine"
            label={i18n.t('substance.drugs.drugType.label.ketamine')}
            value="Ketamine"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="narcotics"
            label={i18n.t('substance.drugs.drugType.label.narcotics')}
            value="Narcotics"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="hallucinogenic"
            label={i18n.t('substance.drugs.drugType.label.hallucinogenic')}
            value="Hallucinogenic"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="steroids"
            label={i18n.t('substance.drugs.drugType.label.steroids')}
            value="Steroids"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="inhalants"
            label={i18n.t('substance.drugs.drugType.label.inhalants')}
            value="Inhalants"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
          <Radio
            className="other"
            label={i18n.t('substance.drugs.drugType.label.other')}
            value="Other"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
          />
        </RadioGroup>
        <Show when={other}>
          <Field
            title={i18n.t('substance.drugs.drugType.label.drugTypeOther')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="DrugTypeOther"
              attribute={`${this.props.attribute}.DrugType`}
              className="drug-type-other"
              value={explanation}
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

DrugType.defaultProps = {
  value: '',
  onError: (value, arr: ErrorCode[]) => arr,
  formType: '',
};
